import { Permission, PermissionKeys } from "../types/permissions";

export const usePermissions = () => {
  const authUserData = localStorage.getItem("authUser");
  const permissions: Permission[] = authUserData
    ? JSON.parse(authUserData).permissions
    : [];
  return permissions;
};

/**
 * Hook that returns a function to check if the user has a specific permission key
 * Usage: const hasPermission = useHasPermission(); hasPermission(PermissionKeys.CLIENTS_VIEW)
 * @returns {(permissionKey: PermissionKeys) => boolean}
 */
export const useHasPermission = () => {
  const permissions = usePermissions();
  return (permissionKey: PermissionKeys): boolean => {
    return permissions.some((perm) => perm.keyName === permissionKey);
  };
};

/**
 * Hook that returns a function to check if the user has any permission from an array of permission keys
 * Usage: const hasAnyPermission = useHasAnyPermission(); hasAnyPermission([PermissionKeys.CLIENTS_VIEW, PermissionKeys.ORDERS_VIEW])
 * @returns {(permissionKeys: PermissionKeys[]) => boolean}
 */
export const useHasAnyPermission = () => {
  const permissions = usePermissions();
  return (permissionKeys: PermissionKeys[]): boolean => {
    return permissionKeys.some((key) =>
      permissions.some((perm) => perm.keyName === key)
    );
  };
};

/**
 * Generic function to check if a permission key or array of keys exists in authUserData permissions
 * Usage:
 *   hasPermission(PermissionKeys.CLIENTS_VIEW)
 *   hasPermission([PermissionKeys.CLIENTS_VIEW, PermissionKeys.ORDERS_VIEW], { mode: "AND" })
 *   hasPermission([PermissionKeys.CLIENTS_VIEW, PermissionKeys.ORDERS_VIEW], { mode: "OR" })
 * @param {PermissionKeys | PermissionKeys[]} permissionKey
 * @param {{ mode?: "AND" | "OR" }} options
 * @returns {boolean}
 */
export function hasPermission(
  permissionKey: PermissionKeys | PermissionKeys[],
  options?: { mode?: "AND" | "OR" }
): boolean {
  const authUserData = localStorage.getItem("authUser");
  const permissions: Permission[] = authUserData
    ? JSON.parse(authUserData).data.permissions
    : [];
  const keys = Array.isArray(permissionKey) ? permissionKey : [permissionKey];
  const mode = options?.mode === "AND" ? "AND" : "OR";
  if (mode === "AND") {
    return keys.every((key) =>
      permissions.some((perm) => perm.keyName === key)
    );
  } else {
    return keys.some((key) => permissions.some((perm) => perm.keyName === key));
  }
}
